import * as Sentry from '@sentry/nextjs';
import {
  FETCH_NO_RESPONSE,
  fetchNoResponseContext,
  recordSameOriginResponse,
  reportedFetchNoResponseErrors,
} from '@/lib/telemetry/fetch-no-response';
import { upstreamPathTemplate } from '@/lib/telemetry/upstream-error';

export { FETCH_NO_RESPONSE } from '@/lib/telemetry/fetch-no-response';

/**
 * The one place a browser request to our own API is made.
 *
 * Every data fetch in the app funnels through here so that a failure is
 * legible in the browser without the calling component doing anything: the
 * thrown error names the endpoint and the server's error code, and the failure
 * is reported once whether or not the caller renders the query's error.
 */

/**
 * The `{ error: '…' }` code the route handlers return
 * (`monolithRouteErrorResponse`), e.g. `session_expired`, `internal_error`.
 */
export function errorCode(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const code = (body as { error: unknown }).error;
    if (typeof code === 'string' && code) {
      return code;
    }
    // Calendar mutation responses can carry { error: { message } }.
    if (code && typeof code === 'object' && 'message' in code) {
      const message = (code as { message: unknown }).message;
      if (typeof message === 'string' && message) {
        return message;
      }
    }
  }
  return undefined;
}

export class ApiFetchError extends Error {
  readonly status: number;
  readonly body?: unknown;
  /**
   * The request URL. Present whenever the error came from `apiFetch`;
   * optional so hand-constructed errors (tests) stay valid.
   */
  readonly url?: string;
  readonly method: string;

  constructor(
    status: number,
    body?: unknown,
    url?: string,
    method: string = 'GET'
  ) {
    // The message is the whole point of this class in the browser: it has to
    // name the failing endpoint and the server's error code, because that is
    // all a developer sees when a query surfaces a rejected fetch in the console
    // or an error boundary. "Request failed with status 500" identifies
    // nothing on a page that fires several requests.
    const code = errorCode(body);
    super(
      url
        ? `${method} ${url} → ${status}${code ? ` (${code})` : ''}`
        : `Request failed with status ${status}`
    );
    this.name = 'ApiFetchError';
    this.status = status;
    this.body = body;
    this.url = url;
    this.method = method;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

/**
 * Logged at the single choke point every request passes through, so a failure
 * is visible even when the caller does not render its error state.
 *
 * Unconditional rather than dev-only: a production failure is exactly when
 * someone is looking at the console.
 */
function logFailure(error: ApiFetchError): void {
  console.error(error.message, {
    url: error.url,
    method: error.method,
    status: error.status,
    body: error.body,
  });
}

/**
 * The expired-session 401: `redirectIfSessionExpired` has already sent the tab
 * to /sign-in, so it is expected and reports nothing. Any other 401, such as
 * an impersonation `unauthorized`, is a real failure.
 */
export function isSessionExpired(error: unknown): boolean {
  return (
    error instanceof ApiFetchError &&
    error.status === 401 &&
    errorCode(error.body) === 'session_expired'
  );
}

export function redirectIfSessionExpired(
  status: number,
  body: unknown,
  url: string,
  optIn = false
): boolean {
  if (
    status !== 401 ||
    errorCode(body) !== 'session_expired' ||
    url.split('?')[0] === '/api/v1/login-lookup' ||
    (!optIn && !url.startsWith('/api/v1/'))
  ) {
    return false;
  }
  window.location.assign('/sign-in');
  return true;
}

export interface ApiFetchOptions {
  /** Inspect status/headers without bypassing apiFetch's error handling. */
  onResponse?: (response: Response) => void;
  /** Binary endpoints such as applicant print return bytes, not JSON. */
  responseType?: 'arrayBuffer';
  /**
   * Extend the session-expired redirect to non-v1 endpoints. All v1 session
   * responses redirect whether or not this option is set.
   */
  redirectOn401?: boolean;
  /**
   * Aborts the request. TanStack Query hands its per-query signal to the
   * `queryFn` and aborts it on unmount or key change, so a stale keystroke or
   * a closed dialog cancels the in-flight request instead of finishing it.
   * Takes precedence over `init.signal`.
   */
  signal?: AbortSignal;
  init?: RequestInit;
}

/**
 * Non-2xx → throws `ApiFetchError`; 204 → `null`; other 2xx → parsed JSON,
 * or an `ArrayBuffer` when `responseType` is `'arrayBuffer'`.
 *
 * An aborted request rejects with the `AbortError` `fetch` itself throws — not
 * an `ApiFetchError` — so a cancellation is never logged or reported as a
 * failed read.
 */
export function apiFetch(
  url: string,
  options: ApiFetchOptions & { responseType: 'arrayBuffer' }
): Promise<ArrayBuffer | null>;
export function apiFetch(
  url: string,
  options?: ApiFetchOptions
): Promise<unknown>;
export async function apiFetch(
  url: string,
  {
    redirectOn401 = false,
    responseType,
    signal,
    init,
    onResponse,
  }: ApiFetchOptions = {}
): Promise<unknown> {
  const method = init?.method ?? 'GET';
  let status: number | undefined;
  let edgeGatewayResponse = false;
  let readingSuccessBody = false;
  try {
    const res = await fetch(url, signal ? { ...init, signal } : init);
    recordSameOriginResponse(url);
    status = res.status;
    edgeGatewayResponse =
      [502, 503, 504].includes(status) &&
      !res.headers.has('x-trace-id') &&
      res.headers.get('x-hire-go-error') !== '1';
    onResponse?.(res);

    const body = !res.ok ? await parseBody(res) : undefined;
    if (redirectIfSessionExpired(res.status, body, url, redirectOn401)) {
      // Only the constant code, never the rest of the response: it can quote
      // upstream fields. The code is what `isSessionExpired` reads.
      const error = new ApiFetchError(
        401,
        { error: 'session_expired' },
        url,
        method
      );
      logFailure(error);
      throw error;
    }

    if (!res.ok) {
      const error = new ApiFetchError(res.status, body, url, method);
      logFailure(error);
      throw error;
    }

    if (res.status === 204) {
      return null;
    }
    readingSuccessBody = true;
    return await (responseType === 'arrayBuffer'
      ? res.arrayBuffer()
      : res.json());
  } catch (error) {
    if (
      !isSessionExpired(error) &&
      // The server keeps a monolith 4xx out of Sentry too: a missing record, a
      // permission miss or a validation refusal is an expected answer. A caller
      // that needs one reports it with its own context.
      !(error instanceof ApiFetchError && error.status < 500) &&
      !(signal ?? init?.signal)?.aborted &&
      !(
        error &&
        typeof error === 'object' &&
        'name' in error &&
        error.name === 'AbortError'
      )
    ) {
      const path = url.split(/[?#]/)[0];
      const template = upstreamPathTemplate(path);
      // The caller's error can quote response fields and query values in its
      // message and own properties. Only this separate exception leaves the tab.
      const reportedError = new Error(
        `${method} ${template} → ${status ?? 'request failed'}`
      );
      // A body stream that breaks after the headers rejects with a TypeError:
      // the same lost connection as a fetch with no response, typically a tab
      // waking from sleep, which drops every in-flight read at once. A body
      // that arrives but does not parse is a SyntaxError and stays an error.
      // Only a read qualifies: a mutation Hire may have committed stays an
      // error in its endpoint's issue, since a manual retry can repeat it.
      const isRead = method === 'GET' || method === 'HEAD';
      const lostBody =
        isRead && readingSuccessBody && error instanceof TypeError;
      const noResponse = status === undefined || lostBody;
      if (noResponse && error instanceof Error) {
        reportedFetchNoResponseErrors.add(error);
      }
      const noResponseContext = noResponse
        ? fetchNoResponseContext(error)
        : undefined;
      // The status alone reads like a success, so the name says the body
      // failed to parse.
      reportedError.name = noResponse
        ? FETCH_NO_RESPONSE
        : error instanceof ApiFetchError
          ? 'ApiFetchError'
          : readingSuccessBody && error instanceof SyntaxError
            ? 'ApiResponseParseError'
            : 'Error';
      Sentry.captureException(reportedError, {
        // Cloudflare answers every origin failure with a status, so a request
        // with none died between the browser and the edge. One alone is
        // noise; a metric alert on its exception type catches a burst across
        // users. Below error level the issue alert rules skip it, and one
        // fingerprint keeps every endpoint in one issue. Gateway responses
        // (502/503/504) without an origin marker share one issue per status;
        // an edge failure says nothing about the endpoint it hit. Origin
        // responses group by endpoint and status class, even without tracing.
        ...(status === undefined || lostBody
          ? noResponseContext
          : {
              fingerprint: edgeGatewayResponse
                ? ['api-fetch', 'gateway', String(status)]
                : [
                    'api-fetch',
                    method,
                    template,
                    `${Math.floor(status / 100)}xx`,
                  ],
            }),
        tags: {
          feature: 'api-fetch',
          ...noResponseContext?.tags,
          // A lost body keeps its endpoint so it stays separable in the
          // shared issue.
          ...(lostBody ? { fetch_phase: 'body' } : {}),
          ...(status !== undefined
            ? { api_path: template, api_status: String(status) }
            : {}),
        },
        extra: { method, status, path },
      });
    }
    throw error;
  }
}
